import { z } from 'zod';
import { CursorPagination } from './common.js';
/**
 * Resource discovery — public bios / chat descriptions mined for channel
 * and group references. See apps/server/src/modules/discovery.
 */
export const TgChatResolveStatus = z.enum(['pending', 'resolved', 'preview', 'failed']);
export const DiscoveredChatSummary = z.object({
    id: z.string().uuid(),
    tgChatId: z.string().nullable(),
    username: z.string().nullable(),
    inviteHash: z.string().nullable(),
    kind: z.string(),
    title: z.string().nullable(),
    description: z.string().nullable(),
    membersCount: z.number().int().nullable(),
    resolveStatus: TgChatResolveStatus,
    resolveError: z.string().nullable(),
    resolvedAt: z.string().datetime().nullable(),
    discoveredCount: z.number().int(),
    depth: z.number().int(),
    firstDiscoveredAt: z.string().datetime(),
    lastDiscoveredAt: z.string().datetime(),
    /** True when a row in `groups` (monitored set) shares this tgChatId. */
    isMonitored: z.boolean(),
    /** Canonical link the operator can paste into "加入群": t.me/<username> or t.me/+<hash>. */
    link: z.string().nullable(),
});
export const ListDiscoveredChatsQuery = CursorPagination.extend({
    kind: z.string().max(16).optional(),
    resolveStatus: TgChatResolveStatus.optional(),
    /** Substring match on title / username / description. */
    q: z.string().max(128).optional(),
    /** Hide chats already present in `groups`. */
    excludeMonitored: z.boolean().optional(),
    sort: z.enum(['discoveredCount', 'membersCount', 'lastDiscoveredAt']).optional(),
});
export const DiscoveredUserSummary = z.object({
    id: z.string().uuid(),
    tgUserId: z.string(),
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    bio: z.string().nullable(),
    isBot: z.boolean(),
    isPremium: z.boolean(),
    firstSource: z.string(),
    bioFetchedAt: z.string().datetime().nullable(),
    linkCount: z.number().int(),
    lastSeenAt: z.string().datetime(),
});
export const ListDiscoveredUsersQuery = CursorPagination.extend({
    /** Only users whose bio produced ≥1 link. */
    withLinks: z.boolean().optional(),
    /** Only users whose bio hasn't been fetched yet. */
    unfetched: z.boolean().optional(),
    q: z.string().max(128).optional(),
});
export const ResourceLinkSummary = z.object({
    id: z.string().uuid(),
    sourceType: z.enum(['user_bio', 'chat_description', 'message', 'web_search']),
    sourceNote: z.string().nullable().optional(),
    sourceUserId: z.string().uuid().nullable(),
    sourceUser: z
        .object({ tgUserId: z.string(), username: z.string().nullable(), firstName: z.string().nullable() })
        .nullable(),
    sourceChatId: z.string().uuid().nullable(),
    sourceChat: z.object({ title: z.string().nullable(), username: z.string().nullable() }).nullable(),
    rawRef: z.string(),
    discoveredAt: z.string().datetime(),
});
export const DiscoveryAccountBudget = z.object({
    accountId: z.string().uuid(),
    phone: z.string(),
    connected: z.boolean(),
    /** getFullUser + getChatMembers claims since local midnight. */
    usedToday: z.number().int(),
    budget: z.number().int(),
    /** resolve_chat claims since local midnight (separate, stricter budget). */
    usedResolveToday: z.number().int(),
    resolveBudget: z.number().int(),
    /** ISO time until which the worker skips this account (FLOOD), or null. */
    cooldownUntil: z.string().datetime().nullable(),
});
export const DiscoveryStats = z.object({
    enabled: z.boolean(),
    users: z.object({
        total: z.number().int(),
        bioFetched: z.number().int(),
        withLinks: z.number().int(),
    }),
    chats: z.object({
        total: z.number().int(),
        resolved: z.number().int(),
        preview: z.number().int(),
        failed: z.number().int(),
        pending: z.number().int(),
        monitored: z.number().int(),
    }),
    links: z.number().int(),
    jobs: z.object({
        pending: z.number().int(),
        running: z.number().int(),
        failed: z.number().int(),
        doneToday: z.number().int(),
    }),
    accounts: z.array(DiscoveryAccountBudget),
});
export const DiscoveryBackfillRequest = z.object({
    /** Enqueue fetch_user for leads.targetUserId + tg_users without bio. */
    users: z.boolean().optional(),
    /** Enqueue list_members for every active monitored group. */
    members: z.boolean().optional(),
    /** Re-queue failed jobs (attempts reset). */
    retryFailed: z.boolean().optional(),
});
export const DiscoveryBackfillResponse = z.object({
    enqueuedUsers: z.number().int(),
    enqueuedMemberScans: z.number().int(),
    retriedFailed: z.number().int(),
});
export const DiscoveryJobSummary = z.object({
    id: z.string().uuid(),
    kind: z.enum(['fetch_user', 'resolve_chat', 'list_members']),
    ref: z.string(),
    depth: z.number().int(),
    priority: z.number().int(),
    status: z.enum(['pending', 'running', 'done', 'failed', 'expired']),
    attempts: z.number().int(),
    lastError: z.string().nullable(),
    nextAttemptAt: z.string().datetime(),
    doneAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const ListDiscoveryJobsQuery = CursorPagination.extend({
    status: z.enum(['pending', 'running', 'done', 'failed', 'expired']).optional(),
    kind: z.enum(['fetch_user', 'resolve_chat', 'list_members']).optional(),
});
//# sourceMappingURL=discovery.js.map